/*
 * oj-product.js — <oj-variant-picker>
 *
 * A small, self-contained variant picker for the OJ product page. It does NOT
 * reinvent the cart: the actual add-to-cart still posts through Horizon's
 * <product-form-component> ({% form 'product' %} -> /cart/add). This element only
 * maps the chosen options to a variant and updates the UI:
 *   - the hidden variant id input the form submits
 *   - the price shown on the ADD TO CART bar (server-rendered money string,
 *     never formatted in JS)
 *   - the button label/disabled state (in stock / sold out)
 *   - the selected colour label
 *   - per-value availability (greys out combinations with no stock)
 *   - the URL (?variant=) and a gentle scroll to the variant's image
 */
class OJVariantPicker extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('script[data-oj-variants]');
    try {
      this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    } catch (e) {
      this.variants = [];
    }
    if (!this.variants.length) return;

    this.section = this.closest('section') || document;
    this.buttons = Array.from(this.querySelectorAll('[data-oj-option]'));

    // Selected option value per 1-based position, seeded from the markup.
    this.selected = [];
    this.buttons.forEach((btn) => {
      if (btn.getAttribute('aria-checked') === 'true') {
        this.selected[Number(btn.dataset.ojOption)] = btn.dataset.ojValue;
      }
    });

    this.controller = new AbortController();
    this.addEventListener('click', this.onClick, { signal: this.controller.signal });

    this.initialised = false;
    this.update();
    this.initialised = true;
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  onClick = (event) => {
    const btn = event.target.closest('[data-oj-option]');
    if (!btn) return;
    // Sold-out values stay selectable: customers can view any colourway's
    // images and price — the ADD TO CART bar greys to SOLD OUT instead of
    // the swatch swallowing the click.
    const pos = Number(btn.dataset.ojOption);
    this.selected[pos] = btn.dataset.ojValue;

    // Reflect the checked radio within this option group only.
    this.buttons
      .filter((b) => Number(b.dataset.ojOption) === pos)
      .forEach((b) => b.setAttribute('aria-checked', String(b === btn)));

    this.update(pos);
  };

  /** Find the variant whose ordered options exactly match the current selection. */
  matchVariant() {
    return this.variants.find((v) =>
      v.options.every((opt, i) => this.selected[i + 1] === undefined || this.selected[i + 1] === opt)
    );
  }

  /** A value is available if some in-stock variant keeps every OTHER chosen option. */
  isValueAvailable(pos, value) {
    return this.variants.some((v) => {
      if (!v.available) return false;
      if (v.options[pos - 1] !== value) return false;
      return v.options.every((opt, i) => {
        const p = i + 1;
        if (p === pos) return true;
        return this.selected[p] === undefined || this.selected[p] === opt;
      });
    });
  }

  /**
   * Desktop gallery layout, mirroring the server-side span stamping (of a
   * 4-column grid): 1 visible image = full width (4); 2 = stacked full-width (4 each);
   * 4 = 2x2 grid (2 each); 3 or 5+ = hero (4) + halves (2), and when the
   * visible total is even the 2-col remainder is odd, so the last image spans
   * full width instead of leaving a half-empty row. Spans are only ever 2 or
   * 4 — half-width is the floor, so tiles can never render tiny. Mobile
   * ignores the spans (swipe carousel). Idempotent; re-run after every
   * colour switch.
   */
  applyGalleryLayout() {
    const items = Array.from(this.section.querySelectorAll('.oj-product__media-item')).filter(
      (el) => !el.hasAttribute('data-oj-hidden')
    );
    const n = items.length;
    items.forEach((el, i) => {
      let cols = 2;
      if (n === 1 || n === 2) cols = 4;
      else if (n === 4) cols = 2;
      else if (i === 0) cols = 4;
      else if (n % 2 === 0 && i === n - 1) cols = 4;
      el.setAttribute('data-oj-cols', String(cols));
    });
  }

  update(changedPos) {
    const isDisabled = (btn) => btn.getAttribute('aria-disabled') === 'true';

    // Per-value availability (cross-option), as an accessible state — the
    // buttons stay focusable so AT users can hear "unavailable".
    const paintAvailability = () => {
      this.buttons.forEach((btn) => {
        const pos = Number(btn.dataset.ojOption);
        const ok = this.isValueAvailable(pos, btn.dataset.ojValue);
        btn.setAttribute('aria-disabled', String(!ok));
        const base = btn.dataset.ojBaseLabel || (btn.dataset.ojBaseLabel = btn.getAttribute('aria-label') || '');
        btn.setAttribute('aria-label', ok ? base : base + ' — unavailable');
      });
    };
    paintAvailability();

    // After a user change, reconcile selections it invalidated: if a value
    // chosen in another group is now out of stock for the new pick, advance to
    // the first available value there (or clear it). Never touch the group the
    // user just set, and never recurse.
    if (changedPos) {
      const positions = [...new Set(this.buttons.map((b) => Number(b.dataset.ojOption)))];
      positions.forEach((pos) => {
        if (pos === changedPos) return;
        const group = this.buttons.filter((b) => Number(b.dataset.ojOption) === pos);
        const current = group.find((b) => b.dataset.ojValue === this.selected[pos]);
        if (current && !isDisabled(current)) return;
        const next = group.find((b) => !isDisabled(b));
        this.selected[pos] = next ? next.dataset.ojValue : undefined;
        group.forEach((b) => b.setAttribute('aria-checked', String(b === next)));
      });
      // Re-evaluate greying against the reconciled selection (matters for 3+ options).
      paintAvailability();
    }

    const variant = this.matchVariant();
    const form = this.section.querySelector('[data-oj-variant-id]');
    const atc = this.section.querySelector('[data-oj-atc]');
    const priceEl = this.section.querySelector('[data-oj-atc-price]');
    const labelEl = this.section.querySelector('[data-oj-atc-label]');

    if (form) form.value = variant ? variant.id : '';

    if (atc && labelEl) {
      if (variant && variant.available) {
        atc.disabled = false;
        labelEl.textContent = atc.dataset.atcAdd;
      } else {
        atc.disabled = true;
        labelEl.textContent = atc.dataset.atcSoldout;
      }
    }
    if (priceEl) priceEl.textContent = variant ? variant.price : '';

    // Compare-at (sale) price — server-formatted string from the variants JSON.
    const compareEl = this.section.querySelector('[data-oj-atc-compare]');
    if (compareEl) {
      const compare = variant && variant.compare_at;
      compareEl.hidden = !compare;
      compareEl.textContent = compare || '';
    }

    // Keep Klarna on-site messaging in step with the selected variant.
    const klarna = this.section.querySelector('[data-oj-klarna]');
    if (klarna && variant && variant.price_raw != null) {
      klarna.setAttribute('data-purchase-amount', String(variant.price_raw));
      window.Klarna?.OnsiteMessaging?.refresh?.();
    }

    // Announce the change in Horizon's own event shape so passive listeners
    // (back-in-stock panel, apps) stay in sync. Plain Event + detail — this
    // file is a classic script, so we don't import VariantUpdateEvent.
    const productId = this.section.querySelector('product-form-component')?.dataset.productId;
    const evt = new Event('variant:update', { bubbles: true });
    evt.detail = {
      resource: variant
        ? { id: variant.id, title: variant.options.join(' / '), available: variant.available }
        : null,
      sourceId: this.section.id,
      data: { productId },
    };
    document.dispatchEvent(evt);

    // Selected colour label(s).
    this.querySelectorAll('[data-oj-option-value]').forEach((el) => {
      const pos = Number(el.dataset.ojOptionValue);
      if (this.selected[pos]) el.textContent = this.selected[pos];
    });

    // Reflect the variant in the URL.
    if (variant && this.initialised) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    // Colour grouping: show only the selected colour's block. data-colour-anchor
    // is a space-separated list of the variant-featured media ids that map to the
    // block ('0' = shared, always shown); a colour's block carries every one of
    // its variants' featured ids, so de-duplicated/size-variant images still match.
    if (variant && variant.media_id != null) {
      const sel = String(variant.media_id);
      this.section.querySelectorAll('[data-colour-anchor]').forEach((el) => {
        const anchors = el.dataset.colourAnchor.split(' ');
        el.toggleAttribute('data-oj-hidden', !(anchors.includes('0') || anchors.includes(sel)));
      });
    }
    this.applyGalleryLayout();

    // Bring the variant's image into view when a colour changes — desktop moves
    // the vertical stack; mobile moves ONLY the carousel track (never the page).
    if (variant && variant.media_id != null && this.initialised && changedPos) {
      const target =
        this.section.querySelector(`.oj-product__media-item[data-media-id="${variant.media_id}"]:not([data-oj-hidden])`) ||
        this.section.querySelector('.oj-product__media-item:not([data-oj-hidden])');
      if (target) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const behavior = reduce ? 'auto' : 'smooth';
        if (window.matchMedia('(min-width: 750px)').matches) {
          target.scrollIntoView({ behavior, block: 'nearest' });
        } else {
          const track = this.section.querySelector('[data-oj-gallery]');
          if (track) {
            const left = track.scrollLeft + target.getBoundingClientRect().left - track.getBoundingClientRect().left;
            track.scrollTo({ left, behavior });
          }
        }
      }
    }
  }
}

if (!customElements.get('oj-variant-picker')) {
  customElements.define('oj-variant-picker', OJVariantPicker);
}

/*
 * <oj-gallery> — pagination dots for the mobile image carousel.
 *
 * The carousel itself is pure CSS scroll-snap (no JS needed to swipe). This
 * element only keeps the dots in sync: it highlights the dot for whichever
 * slide is in view (IntersectionObserver) and scrolls to a slide when a dot is
 * tapped. On desktop the dots are hidden by CSS, so this is a no-op there.
 */
class OJGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-oj-gallery]');
    this.dots = Array.from(this.querySelectorAll('[data-oj-dot]'));
    this.slides = Array.from(this.querySelectorAll('.oj-product__media-item'));
    if (!this.track || this.dots.length < 2 || this.slides.length < 2) return;

    this.controller = new AbortController();
    this.addEventListener('click', this.onDotClick, { signal: this.controller.signal });

    // Track each slide's latest visibility ratio and highlight the most-visible
    // one, so the active dot stays correct even mid-drag between snap points.
    this.ratios = new Map();
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        let bestIdx = -1;
        let best = 0;
        this.slides.forEach((slide, i) => {
          const r = this.ratios.get(slide) || 0;
          if (r > best) {
            best = r;
            bestIdx = i;
          }
        });
        if (bestIdx > -1) this.setActive(bestIdx);
      },
      { root: this.track, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  onDotClick = (event) => {
    const dot = event.target.closest('[data-oj-dot]');
    if (!dot) return;
    const slide = this.slides[Number(dot.dataset.ojDot)];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    slide.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
  };

  setActive(index) {
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
  }
}

if (!customElements.get('oj-gallery')) {
  customElements.define('oj-gallery', OJGallery);
}

/*
 * <oj-sticky-atc> — mobile sticky add-to-cart.
 *
 * The section is a size container (container-type: inline-size), which makes
 * it the containing block for position:fixed descendants — so the bar is
 * portaled to <body> on connect and styled by its own unique id. The bar never
 * duplicates commerce logic: its button proxies clicks to the real add-to-cart
 * button, and a MutationObserver mirrors the real button's label, price and
 * disabled state (oj-variant-picker only updates the first [data-oj-atc] it
 * finds, so the bar watches rather than registers). Visibility comes from an
 * IntersectionObserver on the buy box: shown only once the buy box has
 * scrolled up out of view. Display ≤749px is enforced in CSS.
 */
class OJStickyAtc extends HTMLElement {
  connectedCallback() {
    // Merchants drag sections in the editor; a fixed bar over the canvas is churn.
    if (window.Shopify && window.Shopify.designMode) return;

    const template = this.querySelector('template');
    const section = this.closest('section');
    this.realBtn = section?.querySelector('[data-oj-atc]');
    this.buyBox = section?.querySelector('.oj-product__buy');
    if (!template || !this.realBtn || !this.buyBox) return;

    // Portal to <body>, replacing any bar left by an earlier render of this section.
    const barId = this.dataset.barId;
    document.getElementById(barId)?.remove();
    this.bar = template.content.firstElementChild.cloneNode(true);
    document.body.appendChild(this.bar);

    this.controller = new AbortController();
    this.bar.querySelector('[data-oj-sticky-btn]').addEventListener(
      'click',
      () => this.realBtn.click(),
      { signal: this.controller.signal }
    );

    // Mirror label / price / disabled from the real button.
    this.mirror = new MutationObserver(() => this.sync());
    this.mirror.observe(this.realBtn, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      characterData: true,
      subtree: true,
    });
    this.sync();

    // Show only once the buy box has scrolled up past the viewport top.
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        const passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.bar.toggleAttribute('hidden', !passed);
      },
      { threshold: 0 }
    );
    this.observer.observe(this.buyBox);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.mirror?.disconnect();
    this.observer?.disconnect();
    this.bar?.remove();
    this.bar = null;
  }

  sync() {
    if (!this.bar) return;
    const label = this.realBtn.querySelector('[data-oj-atc-label]')?.textContent;
    const price = this.realBtn.querySelector('[data-oj-atc-price]')?.textContent;
    const btn = this.bar.querySelector('[data-oj-sticky-btn]');
    const labelEl = this.bar.querySelector('[data-oj-sticky-label]');
    const priceEl = this.bar.querySelector('[data-oj-sticky-price]');
    if (label && labelEl) labelEl.textContent = label;
    if (priceEl) priceEl.textContent = price || '';
    btn.disabled = this.realBtn.disabled;
  }
}

if (!customElements.get('oj-sticky-atc')) {
  customElements.define('oj-sticky-atc', OJStickyAtc);
}
